import { pick, keys } from 'ramda';

import { Precision, Compare, Timespan, Metric } from '../constants';
import { timespanPrecisionPresets, comparePresets } from '../utils/timePresets';
import { createActionTypes } from '../actions/optionsBase';
import {
  totalTimespanInterval,
  compareIntervals,
  totalCompareInterval,
  isValidCompare,
} from '../utils/intervals';
import { convertToYearlyPrecision } from '../utils/time';

/*
 * Takes state and resets compare if it is invalid.
 */
export const checkCompareForReset = (state, timeBounds) => {
  const { timespan, customTimespanInterval, compare } = state;

  if (isValidCompare(timeBounds, timespan, customTimespanInterval, compare)) {
    return state;
  }

  return { ...state, compare: Compare.NONE };
};

/*
 * Ensures, that custom timespan and compre intervals match current precision.
 */
export const convertIntervalsPrecision = (state, timeBounds) => {
  const { precision, customTimespanInterval, customCompareInterval } = state;

  if (precision === Precision.MONTHLY) {
    return state;
  }

  return {
    ...state,

    customTimespanInterval:
      customTimespanInterval &&
      convertToYearlyPrecision(customTimespanInterval, timeBounds),

    customCompareInterval:
      customCompareInterval &&
      convertToYearlyPrecision(customCompareInterval, timeBounds),
  };
};

export const timespanInterval = (state, payload) =>
  totalTimespanInterval(
    state.timespan,
    payload.timeBounds,
    state.customTimespanInterval,
  );

export const compareInterval = (state, payload) =>
  totalCompareInterval(
    compareIntervals(
      timespanInterval(state, payload),
      state.compare,
      state.customCompareInterval,
      payload.timeBounds,
    ),
  );

export const initialState = {
  crimeType: 400,
  metric: Metric.TOTAL,
  precision: Precision.MONTHLY,
  timespan: Timespan.LAST_MONTH,
  compare: Compare.NONE,
  customTimespanInterval: null,
  customCompareInterval: null,
};

/*
 * Returns base fields. It will be used to copy data from options to dataSettingsDialog.
 */
export const baseState = state => pick(keys(initialState), state);

export default prefix => {
  const actionTypes = createActionTypes(prefix);

  return (state, action) => {
    const { type, payload } = action;

    switch (type) {
      case actionTypes.SET_METRIC:
        return {
          ...state,
          metric: payload,
        };

      case actionTypes.SET_CRIME_TYPE:
        return {
          ...state,
          crimeType: payload,
        };

      case actionTypes.SET_PRECISION:
        return checkCompareForReset(
          convertIntervalsPrecision(
            {
              ...state,
              precision: payload.precision,
              timespan: Timespan.CUSTOM,
              customTimespanInterval: timespanInterval(state, payload),
            },
            payload.timeBounds,
          ),
          payload.timeBounds,
        );

      case actionTypes.SET_TIMESPAN:
        return checkCompareForReset(
          convertIntervalsPrecision(
            payload.timespan === Timespan.CUSTOM
              ? {
                  ...state,
                  timespan: Timespan.CUSTOM,
                  customTimespanInterval: timespanInterval(state, payload),
                }
              : {
                  ...state,
                  timespan: payload.timespan,
                  precision: timespanPrecisionPresets[payload.timespan],
                },
            payload.timeBounds,
          ),
          payload.timeBounds,
        );

      case actionTypes.SET_COMPARE:
        return checkCompareForReset(
          {
            ...state,
            compare: payload.compare,
            customCompareInterval:
              payload.compare !== Compare.CUSTOM
                ? null
                : state.compare === Compare.YEAR ||
                  state.compare === Compare.PERIOD
                  ? compareInterval(state, payload)
                  : comparePresets[Compare.CUSTOM](
                      timespanInterval(state, payload),
                      payload.timeBounds,
                    )[0],
          },
          payload.timeBounds,
        );

      case actionTypes.SET_CUSTOM_TIMESPAN_INTERVAL:
        return checkCompareForReset(
          {
            ...state,
            timespan: Timespan.CUSTOM,
            customTimespanInterval: payload.interval,
          },
          payload.timeBounds,
        );

      case actionTypes.SET_CUSTOM_COMPARE_INTERVAL:
        return {
          ...state,
          compare: Compare.CUSTOM,
          customCompareInterval: payload,
        };

      default:
        return state;
    }
  };
};



// WEBPACK FOOTER //
// ./src/reducers/optionsBase.js